import React, { useState } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";
import { injectIntl, intlShape } from "react-intl";
import Checkbox from "@material-ui/core/Checkbox";
import Table from "../../ui/table";
import BigComment from "../../ui/big-comment";
import BottomActions from "./proposal-details/bottom-action";
import ExpandedRow from "./expanded-row";
import {
  toFormattedNumber,
  toHibridAccountingkeyFormat,
  toFormattedDate,
  toHibridSocietyFormat
} from "../../../utils/format";
import ExpandButton from "../reconciliation/expand-button";
import { calculateCurency } from ".";

export const mapper = ({
  selected,
  CO_ACCOUNTING_KEY: accountingKey,
  DE_REFERENCE_DOCUMENT: referenceNumber,
  FX_DOCUMENT_DATE: date,
  FX_EXPIRATION_NET: expiringDate,
  DE_POSITION_TEXT,
  AM_LOCAL_CURRENCY: localAmount,
  AM_FOREIGN_CURRENCY: foreignAmount,
  CO_CURRENCY: foreignCurrency,
  CO_LOCAL_CURRENCY: localCurrency,
  CO_SOCIETY: society,
  CO_DIVISION: division,
  CO_CLAIM_AREA: claimArea,
  QT_POSITION,
  ID_PERIOD,
  CO_PERIOD_VARIANT,
  CO_SOCIETY,
  ID_CUSTOMER,
  CO_DOCUMENT_NUMBER,
  QT_EXPIRATION_POSITION,
  sapCode
}) => ({
  selected,
  accountingKey,
  referenceNumber,
  society,
  expiringDate,
  comments: DE_POSITION_TEXT || "",
  amount: foreignAmount || localAmount,
  currency: foreignCurrency || localCurrency || "EUR",
  sapCode,
  itemObject: {
    date,
    society,
    division,
    claimArea,
    QT_POSITION,
    ID_PERIOD,
    CO_PERIOD_VARIANT,
    CO_SOCIETY,
    ID_CUSTOMER,
    CO_DOCUMENT_NUMBER,
    QT_EXPIRATION_POSITION
  }
});

const Records = ({
  intl,
  data,
  toggleSelect,
  extraToolbarOptions,
  currency,
  showBottomActions,
  addTransfersToProposal,
  tableOptions,
  selectedTransfer,
  noExpand,
  withSapCode
}) => {
  const { messages } = intl;
  const currencyText = currency ? calculateCurency(currency) : "€";
  const [expandedRows, setExpandedRows] = useState([]);

  const sortUnselectedFirst = (a, b) => {
    if (!a.selected) {
      return -1;
    }
    // eslint-disable-next-line eqeqeq
    if (a.selected == b.selected) {
      return 0;
    }
    if (a.selected) {
      return 1;
    }
    return 0;
  };
  const customTableOptions = {
    ...tableOptions,
    expandedRows,
    extraToolbarOptions
  };
  const columns = [
    {
      name: "selected",
      label: " ",
      options: {
        filter: false,
        cellRenderer: value => {
          const { original } = value.row;
          return (
            original &&
            original.accountingKey !== "09" &&
            !selectedTransfer.includes(original.itemObject.CO_DOCUMENT_NUMBER) && (
              <Checkbox
                edge="start"
                className="input-check"
                checked={value.row.original.selected}
                onChange={() => toggleSelect(value.row.original.itemObject)}
              />
            )
          );
        }
      }
    },
    {
      name: "sapCode",
      label: messages.sapCode,
      options: {
        filter: true
      }
    },
    {
      name: "accountingKey",
      label: messages.accountingKey,
      options: {
        filter: true,
        cellRenderer: value => toHibridAccountingkeyFormat(value.row.original.accountingKey),
        filterRenderer: value => toHibridAccountingkeyFormat(value)
      }
    },
    {
      name: "referenceNumber",
      label: messages.referenceNumber,
      options: {
        filter: true
      }
    },
    {
      name: "society",
      label: messages.society,
      options: {
        filter: true,
        show: true,
        weigth: 4,
        cellRenderer: value => toHibridSocietyFormat(value.row.original.society),
        filterRenderer: value => toHibridSocietyFormat(value)
      }
    },
    {
      name: "expiringDate",
      label: messages.expirationDate,
      options: {
        filter: true,
        type: "date",
        cellClass: "no-wrap",
        cellRenderer: value => toFormattedDate(value.row.original.expiringDate),
        filterRenderer: value => `${messages.expirationDate}: ${value.startDate} - ${value.endDate}`
      }
    },
    {
      name: "comments",
      label: messages.concept,
      options: {
        filter: false,
        cellRenderer: value => <BigComment comment={value.row.original.comments} maxLength={107} />
      }
    },
    {
      name: "amount",
      label: messages.amount,
      options: {
        type: "number",
        filter: true,
        cellRenderer: value => {
          let rowCurrency = currencyText;
          const original = value.row.original.currency;
          if (original) {
            rowCurrency = original ? calculateCurency(currency) : "€";
          }
          return (
            <div className="text-right mr-2">
              {`${toFormattedNumber(value.row.original.amount)}${rowCurrency}`}
            </div>
          );
        }
      }
    },
    {
      label: messages.empty,
      options: {
        sort: false,
        filter: false,
        cellClass: "short-column",
        cellRenderer: value =>
          !noExpand ? (
            <ExpandButton
              toggleRow={() => {
                const newRows = expandedRows.slice(0, expandedRows.length);
                if (newRows.includes(value.row.original.myTableIndex)) {
                  newRows.splice(newRows.indexOf(value.row.original.myTableIndex), 1);
                } else {
                  newRows.push(value.row.original.myTableIndex);
                }
                setExpandedRows(newRows);
              }}
              isExpanded={expandedRows.includes(value.row.index)}
            />
          ) : null
      }
    },
    {
      label: messages.empty,
      options: {
        sort: false,
        filter: false,
        expandedColumn: true,
        cellRenderer: value => <ExpandedRow data={value.row.original.itemObject} />
      }
    }
  ];

  return (
    <Table
      placeHolder="Buscar por clave, referencia..."
      className="records"
      columns={columns.filter(x => withSapCode || x.name !== "sapCode")}
      data={data ? R.reverse()(data.map(mapper).sort(sortUnselectedFirst)) : []}
      options={customTableOptions}
      enableGoogleAnalytics
      bottomActions={showBottomActions ? <BottomActions onClick={addTransfersToProposal} /> : null}
    />
  );
};

Records.propTypes = {
  currency: PropTypes.string,
  intl: intlShape.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  toggleSelect: PropTypes.func,
  extraToolbarOptions: PropTypes.node,
  showBottomActions: PropTypes.bool,
  addTransfersToProposal: PropTypes.func,
  tableOptions: PropTypes.shape(),
  selectedTransfer: PropTypes.arrayOf(),
  noExpand: PropTypes.bool,
  withSapCode: PropTypes.bool
};

Records.defaultProps = {
  currency: "EUR",
  data: [],
  toggleSelect: () => {},
  extraToolbarOptions: undefined,
  showBottomActions: false,
  addTransfersToProposal: () => {},
  tableOptions: {
    emptyElement: <span>No hay elementos para el cliente</span>
  },
  selectedTransfer: [],
  noExpand: false,
  withSapCode: false
};

export default injectIntl(Records);
